import React from 'react';
import { Container, Typography, Box, List, ListItem, ListItemText } from '@mui/material';

const TermsAndConditions = () => {
  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#3e31bc' }}>
          Terms and Conditions for Blink Buy Dropshipping
        </Typography>
        
        <Typography variant="body1" paragraph>
          These Terms and Conditions ("Agreement") govern your use of Blink Buy Dropshipping services, including the listing, marketing, and sale of products on various e-commerce platforms using the dropshipping business model. By accessing, registering for, or using the Blink Buy Dropshipping platform ("Platform" or "Service"), you ("User," "Seller," or "you") acknowledge that you have read, understood, and agreed to be legally bound by this Agreement. If you do not agree with these Terms and Conditions, you must immediately discontinue use of the Service.
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
          Please review this Agreement carefully before engaging in any selling or business activities through Blink Buy Dropshipping.
        </Typography>

        {/* Section 1 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          1. Services Provided
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping provides an end-to-end e-commerce solution that allows individuals and businesses to sell products without maintaining physical inventory. Through our services, sellers may list products sourced from third-party suppliers, and upon receiving an order, the supplier will ship the product directly to the customer. This model eliminates inventory costs, warehousing, and logistical handling.
        </Typography>
        
        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold' }}>
          Our platform provides:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Access to reliable suppliers and product databases" />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Automated order management tools" />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Customer service support" />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Business guidance, analytics, and operational tools" />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Wallet and payment management services" />
          </ListItem>
        </List>

        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
          Source of Products:
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping sources products from leading, well-known global e-commerce platforms, including:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Amazon" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• AliExpress" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Alibaba" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Walmart" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• eBay" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          These platforms provide access to a wide range of high-quality and verified products.
        </Typography>
        <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', color: '#666' }}>
          <strong>Note:</strong> Blink Buy Dropshipping is not legally affiliated, endorsed, or partnered with Amazon, AliExpress, Alibaba, Walmart, or eBay. Products available on our platform originate from these public marketplaces through legitimate sourcing channels.
        </Typography>

        {/* Section 2 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          2. Eligibility
        </Typography>
        <Typography variant="body1" paragraph>
          To access or use Blink Buy Dropshipping services, you must:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Be at least 18 years of age or the legal age of majority in your country" />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Have the legal capacity to enter into binding contracts" />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary="• Provide accurate, complete, and updated personal or business information during registration" />
          </ListItem>
        </List>
        <Typography variant="body1" paragraph>
          By using the platform, you represent that you meet all eligibility requirements. Blink Buy Dropshipping reserves the right to reject, suspend, or terminate accounts that provide false information or violate eligibility standards.
        </Typography>

        {/* Section 3 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          3. Account Registration
        </Typography>
        <Typography variant="body1" paragraph>
          To use the Service, you must register for an account and provide necessary details including, but not limited to:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Full legal name" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Contact information" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Payment and withdrawal preferences" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Tax identification data, where required" /></ListItem>
        </List>
        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
          You are responsible for:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Keeping your login credentials confidential" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Ensuring no unauthorized party accesses your account" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• All actions performed through your account" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          If you suspect unauthorized access or a security breach, you must immediately notify our support team. Blink Buy Dropshipping is not liable for losses resulting from compromised accounts caused by user negligence.
        </Typography>

        {/* Section 4 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          4. Product Listings
        </Typography>
        <Typography variant="body1" paragraph>
          As a seller, you are fully responsible for:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• The accuracy and legality of your product listings" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Ensuring all product descriptions, images, and claims are truthful" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Complying with all local and international laws regarding product safety, labeling, and sales" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Ensuring that your listings do not violate any intellectual property rights" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Adhering strictly to all policies of the platforms where you choose to sell" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping may remove or reject any product listing found to be misleading, unlawful, unsafe, or in violation of third-party rights.
        </Typography>

        {/* Section 5 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          5. Fees and Payments
        </Typography>
        <Typography variant="body1" paragraph>
          By using the Service, you agree to pay all applicable fees, including:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Service subscription fees" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Transaction and processing fees" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Advertising or promotion fees" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Administrative fees" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Supplier or fulfillment-related costs" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          Fees are automatically deducted from your Blink Buy Wallet balance. You agree to maintain sufficient funds to cover these fees at all times.
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping reserves the right to <strong>modify</strong> its fee structure with prior notice. Continued use of the Service after fee updates will constitute acceptance of the new terms.
        </Typography>

        {/* Section 6 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          6. Wallet and Earnings
        </Typography>
        <Typography variant="body1" paragraph>
          All earnings generated from your sales processed through Blink Buy Dropshipping will be credited to your Wallet Balance. Withdrawals must follow the platform policies, including:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Withdrawal verification checks" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Processing timelines" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Minimum withdrawal thresholds" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping is not responsible for delays caused by banks, payment gateways, or compliance reviews.
        </Typography>

        {/* Section 7 - NEW CRITICAL SECTION */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#ff6000', mt: 3 }}>
          7. Early Withdrawals and Store Closure
        </Typography>
        
        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
          Early Withdrawals
        </Typography>
        <Typography variant="body1" paragraph>
          If you request to withdraw funds before the standard withdrawal period, a <strong style={{ color: '#ff6000' }}>20% early withdrawal tax fee</strong> will be applied to the amount you wish to withdraw. This fee covers administrative, operational, and processing costs.
        </Typography>

        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
          Store Closure
        </Typography>
        <Typography variant="body1" paragraph>
          If you decide to close your Blink Buy Dropshipping store:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText primary={
              <span>A <strong style={{ color: '#ff6000' }}>20% tax fee</strong> will be applied to the remaining wallet balance</span>
            } />
          </ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• This fee is non-refundable and non-negotiable" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Upon closure, all remaining wallet balances will be forfeited" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• You will permanently lose access to your account and financial data" /></ListItem>
        </List>
        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', color: '#ff6000', fontWeight: 'bold' }}>
          These policies apply universally to all users without exception.
        </Typography>

        {/* Section 8 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          8. Taxes
        </Typography>
        <Typography variant="body1" paragraph>
          You are solely responsible for calculating, reporting, and paying all applicable taxes related to your earnings, including:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Income tax" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• VAT" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Sales tax" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• GST" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Any jurisdiction-specific taxation" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping does not calculate, withhold, or remit taxes on your behalf. We strongly recommend consulting a certified tax professional.
        </Typography>

        {/* Section 9 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          9. Prohibited Conduct
        </Typography>
        <Typography variant="body1" paragraph>
          You agree not to engage in the following activities:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Selling counterfeit, illegal, or unsafe products" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Violating supplier or marketplace policies" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Providing false or misleading product information" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Engaging in fraud, scam activities, or chargeback abuse" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Manipulating reviews, ratings, or platform algorithms" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Spamming, unauthorized advertising, or abusive behavior" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Attempting to hack, damage, or exploit our systems" /></ListItem>
        </List>
        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', color: '#e63a28' }}>
          Violation of any of the above may result in immediate suspension, termination, and forfeiture of funds.
        </Typography>

        {/* Section 10 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          10. Termination of Services
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping may suspend or terminate your account if:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• You violate these Terms and Conditions" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• You engage in fraud or harmful activities" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Your actions risk damaging the platform, partners, or customers" /></ListItem>
        </List>
        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
          In case of termination:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• You may lose access to your account" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Remaining wallet balances may be forfeited" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• No refunds or compensations will be provided" /></ListItem>
        </List>

        {/* Section 11 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          11. Liability and Disclaimers
        </Typography>
        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold' }}>
          Blink Buy Dropshipping does not guarantee:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Sales performance" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Profit outcomes" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Platform uptime" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Product availability" /></ListItem>
        </List>
        <Typography variant="body1" paragraph>
          You acknowledge that the success of your business depends on market conditions, product demand, management decisions, and external factors beyond our control.
        </Typography>
        <Typography variant="body2" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
          Blink Buy Dropshipping is not liable for:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Loss of profits" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• System issues" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Supplier delays" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Legal disputes involving your products" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Any indirect or consequential damages" /></ListItem>
        </List>

        {/* Section 12 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          12. Indemnification
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to indemnify and hold Blink Buy Dropshipping and its affiliates harmless from any claims, losses, damages, liabilities, or legal fees arising from:
        </Typography>
        <List sx={{ pl: 2 }}>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Your misuse of the Service" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Violation of these terms" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Breach of third-party rights" /></ListItem>
          <ListItem sx={{ py: 0.5 }}><ListItemText primary="• Non-compliance with legal or tax obligations" /></ListItem>
        </List>

        {/* Section 13 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          13. Amendments
        </Typography>
        <Typography variant="body1" paragraph>
          Blink Buy Dropshipping reserves the right to update, modify, or revise this Agreement at any time. Changes will be posted on the platform, and continued use of the Service constitutes your acceptance of the modified terms.
        </Typography>

        {/* Section 14 */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#3e31bc', mt: 3 }}>
          14. Governing Law
        </Typography>
        <Typography variant="body1" paragraph>
          This Agreement shall be governed by and interpreted in accordance with applicable laws. Any disputes will be handled exclusively in the appropriate jurisdiction's courts.
        </Typography>

        {/* Final Statement */}
        <Box sx={{ mt: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', textAlign: 'center', color: '#3e31bc' }}>
            By using Blink Buy Dropshipping services, you acknowledge that you have read, understood, and fully agree to comply with these Terms and Conditions.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default TermsAndConditions;